<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <Breadcrumb :links="breadcrumb" />

      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">
            Relatório de conversas
          </h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Consulte, filtre, exporte e administre os relatórios de conversas.
          </p>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="start">
        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="p-6">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div class="flex flex-col gap-1 xl:col-span-2">
                <label for="busca" class="text-sm font-medium text-gray-500">Buscar</label>
                <input
                  v-model="busca"
                  type="text"
                  id="busca"
                  placeholder="Nome, telefone, contrato, CPF ou protocolo..."
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label for="de" class="text-sm font-medium text-gray-500">A partir de</label>
                <input
                  v-model="de"
                  type="datetime-local"
                  name="de"
                  id="de"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label for="ate" class="text-sm font-medium text-gray-500">Até</label>
                <input
                  v-model="ate"
                  type="datetime-local"
                  name="ate"
                  id="ate"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div v-if="$store.state.user.admin" class="flex flex-col gap-1">
                <label for="centrocusto" class="text-sm font-medium text-gray-500">Centro de custo</label>

                <div class="relative group">
                  <select
                    v-model="centrocusto"
                    name="centrocusto"
                    id="centrocusto"
                    class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
                  >
                    <option :value="null">Todos</option>
                    <option v-for="c in centroscusto" :key="c._id" :value="c._id">
                      {{ c.nome }}
                    </option>
                  </select>

                  <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label for="operador" class="text-sm font-medium text-gray-500">Operador</label>

                <div class="relative group">
                  <select
                    v-model="operador"
                    name="operador"
                    id="operador"
                    class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
                  >
                    <option :value="null">Todos</option>
                    <option v-for="p in operadores" :key="p._id" :value="p._id">
                      {{ p.nome }}
                    </option>
                  </select>

                  <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div class="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <label class="inline-flex items-center text-sm font-medium text-gray-700">
                  <input
                    id="desativo"
                    v-model="desativo"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-100"
                  />
                  <span class="ml-2">Filtrar conversas desativadas</span>
                </label>

                <label class="inline-flex items-center text-sm font-medium text-gray-700">
                  <input
                    id="respondido"
                    v-model="respondido"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-100"
                  />
                  <span class="ml-2">Filtrar conversas respondidas</span>
                </label>
              </div>

              <div class="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 xl:col-span-2">
                <label class="text-sm font-medium text-gray-500">Filtrar último a responder</label>
                <div class="flex flex-wrap gap-4">
                  <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="ultimoAResponder" type="radio" name="ultimoAResponder" value="Ambos" class="h-4 w-4 text-blue-700 focus:ring-blue-100" />
                    <span class="ml-2">Ambos</span>
                  </label>

                  <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="ultimoAResponder" type="radio" name="ultimoAResponder" value="Operador" class="h-4 w-4 text-blue-700 focus:ring-blue-100" />
                    <span class="ml-2">Operador</span>
                  </label>

                  <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="ultimoAResponder" type="radio" name="ultimoAResponder" value="Cliente" class="h-4 w-4 text-blue-700 focus:ring-blue-100" />
                    <span class="ml-2">Cliente</span>
                  </label>
                </div>
              </div>

              <div class="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 xl:col-span-2">
                <label class="text-sm font-medium text-gray-500">Filtrar data</label>
                <div class="flex flex-wrap gap-4">
                  <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="dataMensagem" type="radio" name="dataMensagem" value="ultimo" class="h-4 w-4 text-blue-700 focus:ring-blue-100" />
                    <span class="ml-2">Última mensagem</span>
                  </label>

                  <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="dataMensagem" type="radio" name="dataMensagem" value="primeira" class="h-4 w-4 text-blue-700 focus:ring-blue-100" />
                    <span class="ml-2">Primeira mensagem</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="p-6">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">Buscar conversas</label>
                <button
                  @click="start"
                  type="button"
                  class="h-11 w-full rounded-xl font-medium text-white transition"
                  :style="{ backgroundColor: primaryColor }"
                  @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                  @mouseleave="$event.target.style.backgroundColor = primaryColor"
                >
                  Buscar conversas
                </button>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">Arquivar múltiplas</label>
                <button
                  @click="$modal.show('arquivarMultiplas')"
                  type="button"
                  class="h-11 w-full rounded-xl bg-red-700 font-medium text-white transition hover:bg-red-800"
                >
                  Arquivar conversas
                </button>
              </div>

              <div class="flex flex-col gap-1 xl:col-span-2">
                <label for="email" class="text-sm font-medium text-gray-500">Email download relatório</label>
                <input
                  v-model="email"
                  type="email"
                  name="email"
                  id="email"
                  placeholder="email@exemplo.com"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">Download relatório</label>
                <button
                  @click="downloadRelatorio"
                  target="_blank"
                  type="button"
                  role="button"
                  class="flex h-11 w-full items-center justify-center gap-2 rounded-xl font-medium text-white transition"
                  :style="{ backgroundColor: primaryColor }"
                  @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                  @mouseleave="$event.target.style.backgroundColor = primaryColor"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 3v12m0 0l-3.5-3.5M12 15l3.5-3.5M4 21h16"
                    />
                  </svg>
                  Download conversas
                </button>
              </div>

              <div v-if="isLoadingBusca || isLoading" class="rounded-xl border border-gray-100 bg-gray-50 p-4 xl:col-span-5">
                <p v-if="isLoadingBusca" class="text-sm text-gray-600">
                  <b>Progresso busca:</b>
                  <span class="font-medium text-green-600">{{ progressoBusca > 1 ? progressoBusca : 'Buscando conversas...' }}</span>
                  <span v-if="progressoBusca > 1">/ 100%</span>
                </p>

                <p v-if="isLoading" class="text-sm text-gray-600">
                  <b>Progresso download:</b>
                  <span class="font-medium text-green-600">{{ progresso > 1 ? progresso : 'Buscando conversas...' }}</span>
                  <span v-if="progresso > 1">/ 100%</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <span class="text-sm font-medium text-gray-500">Total conversas</span>
          <p class="mt-2 text-3xl font-semibold text-green-600">{{ totalEnvio }}</p>
        </div>
      </div>

      <div v-if="tags.length" class="grid grid-cols-1 gap-4 md:grid-cols-3 2xl:grid-cols-4">
        <div
          v-for="(item, idx) in tags"
          :key="idx"
          class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
        >
          <label class="block text-lg font-semibold text-gray-800">{{ item.tag }}</label>
          <span class="mt-2 block text-sm font-medium text-gray-500">
            Total conversas:
            <b class="text-green-600">{{ item.total }}</b>
          </span>
        </div>
      </div>

      <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-4 py-4 text-left font-medium">Ativa</th>
                <th class="px-4 py-4 text-left font-medium">Protocolo</th>
                <th class="px-4 py-4 text-left font-medium">Contato</th>
                <th class="px-4 py-4 text-left font-medium">Operador</th>
                <th class="px-4 py-4 text-left font-medium">Centro de Custo</th>
                <th class="px-4 py-4 text-left font-medium">Tags</th>
                <th class="px-4 py-4 text-left font-medium">Início atendimento</th>
                <th class="px-4 py-4 text-left font-medium">Última mensagem (Tempo)</th>
                <th class="px-4 py-4 text-left font-medium">Finalização</th>
                <th class="px-4 py-4 text-left font-medium">Enviadas / Recebidas</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="item in list"
                :key="item._id"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-4 py-5 text-gray-700">
                  <span
                    class="rounded-full px-3 py-1 text-xs font-medium"
                    :class="item.ativa ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                  >
                    {{ item.ativa ? 'Sim' : 'Não' }}
                  </span>
                </td>
                <td class="px-4 py-5 font-medium text-gray-700">{{ item.protocolo ? item.protocolo : '-' }}</td>
                <td class="px-4 py-5 text-gray-700">{{ item.contato ? item.contato : '-' }}</td>
                <td class="px-4 py-5 text-gray-700">{{ item.operador ? item.operador : '-' }}</td>
                <td class="px-4 py-5 text-gray-700">{{ item.centrocusto ? item.centrocusto : '-' }}</td>
                <td class="px-4 py-5 text-gray-700">
                  <span
                    v-for="(tag, idx) in item.tags"
                    :key="idx"
                    class="mb-1 mr-1 inline-block rounded-full border px-3 py-1 text-xs font-medium"
                    :class="tag && tag.editarCorDaEtiqueta ? '' : 'border-blue-200 bg-blue-50 text-blue-700'"
                    :style="getTagChipStyle(tag)"
                  >
                    {{ tag && tag.nome ? tag.nome : '' }}
                  </span>
                  <span v-if="!item.tags || !item.tags.length">-</span>
                </td>
                <td class="px-4 py-5 text-gray-700">{{ item.dataPrimeiroContato | moment('DD/MM/YYYY HH:mm') }}</td>
                <td class="px-4 py-5 text-gray-700">
                  {{ item.dataMensagem | moment('DD/MM/YYYY HH:mm') }}
                  <span class="text-gray-400">({{ returnTempo(item.tempo ? item.tempo : 0) }})</span>
                </td>
                <td class="px-4 py-5 text-gray-700">{{ item.finalizacao ? item.finalizacao : '-' }}</td>
                <td class="px-4 py-5 font-medium text-gray-700">
                  {{ item.totalmsgEnviadas ? item.totalmsgEnviadas : 0 }} /
                  {{ item.totalmsgRecebidas ? item.totalmsgRecebidas : 0 }}
                </td>
              </tr>

              <tr v-if="list.length === 0">
                <td colspan="10" class="px-6 py-12 text-center text-sm text-gray-400">
                  Nenhuma conversa encontrada com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="border-t border-gray-100 px-4 py-4">
          <AppPagination
            :current-page="page"
            :total-pages="totalPages"
            :total-items="total"
            :page-size="perPage"
            item-label="conversas"
            @paginate="setPage"
          />
        </div>
      </div>

      <modal name="arquivarMultiplas" height="auto">
        <div class="space-y-4 px-6 py-6">
          <div>
            <h1 class="text-xl font-semibold text-gray-900">Arquivar contatos</h1>
            <p class="mt-1 text-sm text-gray-500">Informe os números ou selecione um centro de custo para arquivar conversas.</p>
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-1 md:col-span-2">
              <label for="modal-centrocusto" class="text-sm font-medium text-gray-500">Centro de Custo</label>
              <div class="relative group">
                <select
                  v-model="form.centrocusto"
                  required
                  name="centrocusto"
                  id="modal-centrocusto"
                  class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
                >
                  <option :value="null">Verificar em todos</option>
                  <option v-for="c in centroscusto" :key="c._id" :value="c._id">
                    {{ c.nome }}
                  </option>
                </select>

                <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">Número</label>
              <input
                v-model="form.numero"
                type="number"
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                placeholder="Número"
              />
            </div>

            <div class="flex items-end">
              <button
                @click="addNumeros"
                type="button"
                class="h-11 w-full rounded-xl bg-green-600 font-medium text-white transition hover:bg-green-700"
              >
                Adicionar
              </button>
            </div>
          </div>

          <div v-if="numeros.length" class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div v-for="(n, idx) in numeros" :key="idx" class="flex items-center justify-between border-b border-gray-200 py-2 last:border-b-0">
              <label class="text-sm font-medium text-gray-700">{{ n }}</label>
              <button
                @click="numeros.splice(idx, 1)"
                type="button"
                class="rounded-lg bg-red-700 px-4 py-2 text-xs font-medium text-white transition hover:bg-red-800"
              >
                Remover
              </button>
            </div>
          </div>

          <div class="flex flex-col gap-2 sm:flex-row">
            <button
              v-if="numeros.length"
              @click="arquivarMultiplas"
              type="button"
              class="h-11 rounded-xl px-5 font-medium text-white transition"
              :style="{ backgroundColor: primaryColor }"
              @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
              @mouseleave="$event.target.style.backgroundColor = primaryColor"
            >
              Arquivar números
            </button>

            <button
              v-if="form.centrocusto && !numeros.length"
              @click="desativarTodasCentroCusto"
              type="button"
              class="h-11 rounded-xl bg-red-700 px-5 font-medium text-white transition hover:bg-red-800"
            >
              Desativar todas conversas do centro de custo
            </button>
          </div>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import AppPagination from '@/components/AppPagination.vue'
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  components: {
    AppPagination
  },
  data() {
    return {
      isLoading: false,
      isLoadingBusca: false,
      progresso: '',
      progressoBusca: '',
      breadcrumb: [
        { url: '/relatorios/relatorio-chats', value: 'Relatório de conversas' }
      ],
      moment: moment,
      tags: [],
      form: {
        numero: '',
        centrocusto: null
      },
      email: '',
      numeros: [],
      centroscusto: [],
      centrocusto: null,
      ultimoAResponder: 'Ambos',
      dataMensagem: 'ultimo',
      operadores: [],
      operador: null,
      totalEnvio: 0,
      respondido: false,
      desativo: false,
      busca: '',
      list: [],
      total: 0,
      page: 1,
      perPage: 20,
      de: moment().startOf('d').format('YYYY-MM-DDTHH:mm'),
      ate: moment().endOf('d').format('YYYY-MM-DDTHH:mm')
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    }
  },
  methods: {
    getTagChipStyle(tag) {
      if (!tag || !tag.editarCorDaEtiqueta || !tag.corDaEtiqueta) {
        return {}
      }

      const color = this.normalizeHexColor(tag.corDaEtiqueta)
      if (!color) {
        return {}
      }

      return {
        backgroundColor: color,
        borderColor: color,
        color: this.isLightColor(color) ? '#1f2937' : '#ffffff'
      }
    },
    normalizeHexColor(color) {
      const value = String(color || '').trim()
      const shortHex = /^#([0-9a-fA-F]{3})$/
      const longHex = /^#([0-9a-fA-F]{6})$/

      if (shortHex.test(value)) {
        const hex = value.slice(1)
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      }

      if (longHex.test(value)) {
        return value
      }

      return null
    },
    isLightColor(hexColor) {
      const hex = hexColor.slice(1)
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b)

      return luminance > 186
    },
    async start() {
      this.progressoBusca = 1
      this.isLoadingBusca = true
      const listTagsReq = await this.$http.post('/v1/chatv2/relatorio-tags', {
        de: this.de,
        ate: this.ate,
        centrocusto: this.centrocusto,
        operador: this.operador,
        busca: this.busca
      })

      this.tags = listTagsReq.data.tags

      const listDetReq = await this.$http.post('/v1/chatv2/relatorio-detalhado', {
        de: this.de,
        ate: this.ate,
        centrocusto: this.centrocusto,
        ultimoAResponder: this.ultimoAResponder,
        dataMensagem: this.dataMensagem,
        operador: this.operador,
        desativo: this.desativo,
        respondido: this.respondido,
        limit: this.perPage,
        busca: this.busca
      })

      if (!listDetReq.data.success) return this.$vToastify.error(listDetReq.data.err)

      this.list = listDetReq.data.data
      this.total = listDetReq.data.total
      this.totalEnvio = listDetReq.data.total
    },
    async setPage(page) {
      this.page = page
      const skip = (page - 1) * this.perPage

      const listDetReq = await this.$http.post('/v1/chatv2/relatorio-detalhado', {
        de: this.de,
        ate: this.ate,
        centrocusto: this.centrocusto,
        ultimoAResponder: this.ultimoAResponder,
        dataMensagem: this.dataMensagem,
        operador: this.operador,
        desativo: this.desativo,
        respondido: this.respondido,
        busca: this.busca,
        limit: this.perPage,
        skip
      })
      this.list = listDetReq.data.data
      this.total = listDetReq.data.total
    },
    async downloadRelatorio() {
      if (!this.email) return this.$vToastify.error('Email é obrigatório para recebimento!')

      const req = await this.$http.post('/v1/chatv2/relatorio-detalhado/dowload', {
        email: this.email.trim(),
        de: this.de,
        ate: this.ate,
        operador: this.operador,
        centrocusto: this.centrocusto,
        ultimoAResponder: this.ultimoAResponder,
        dataMensagem: this.dataMensagem
      })
      if (req.data.success) {
        this.$vToastify.success(req.data.msg)
        this.email = ''
        this.progresso = 1
        this.isLoading = true
      } else {
        this.$vToastify.error(req.data.err)
      }
    },
    async desativarTodasCentroCusto() {
      if (confirm('Desativar todas as conversas do centro de custo ?')) {
        const req = await this.$http.post('v1/chatV2/arquivarList', { allCentroCusto: true, centrocusto: this.form.centrocusto })
        if (req.data.success) {
          this.$vToastify.success(req.data.msg)
          this.numeros = []
          this.form.numero = ''
          this.$modal.hide('arquivarMultiplas')
        } else {
          this.$vToastify.error(req.data.err)
        }
      }
    },
    async arquivarMultiplas() {
      if (!this.numeros.length) return this.$vToastify.error('Digite os números de contatos para arquivar')
      const req = await this.$http.post('v1/chatV2/arquivarList', { numeros: this.numeros, centrocusto: this.form.centrocusto })

      if (req.data.success) {
        this.$vToastify.success(req.data.msg)
        this.numeros = []
        this.form.numero = ''
        this.$modal.hide('arquivarMultiplas')
      } else {
        this.$vToastify.error(req.data.err)
      }
    },
    async addNumeros() {
      if (!this.form.numero) return this.$vToastify.error('Digite o número')
      this.numeros.push(this.form.numero)
      this.form.numero = ''
    },
    returnTempo(tempo) {
      if (tempo > 60) {
        const horas = tempo / 60
        return `${parseInt(tempo / 60)} hora${horas > 1 ? 's' : ''}${tempo % 60 > 1 ? `, ${tempo % 60} minutos` : ''}`
      } else {
        return `${tempo} minutos`
      }
    }
  },
  async beforeMount() {
    const centroscustoReq = await this.$http.post('/v1/centrocusto/list', { all: true })
    this.centroscusto = centroscustoReq.data.data

    const operadoresReq = await this.$http.post('/v1/operador/list', {
      all: true,
      chatAcesso: true
    })
    this.operadores = operadoresReq.data.data

    if (this.$store.state.user.centrocusto) {
      this.centrocusto = this.$store.state.user.centrocusto
    }

    await this.start()
  },
  async mounted() {
    this.sockets.subscribe(`progressao_conversas_buscar_${this.$store.state.user._id}`, (progresso) => {
      this.progressoBusca = progresso
      this.isLoadingBusca = progresso > 0 && progresso !== 100
    })

    this.sockets.subscribe(`progressao_conversas_${this.$store.state.user._id}`, (progresso) => {
      this.progresso = progresso
      this.isLoading = progresso > 0 && progresso !== 100
    })
  }
}
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

select::-ms-expand {
  display: none;
}

select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
